// src/admin/pages/EmailTemplatesPage.js
// AdminHQ Email Templates - Edit and preview system email templates
import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  Edit, 
  Save, 
  X, 
  FileText, 
  Mail,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

const EmailTemplatesPage = () => {
  const [selectedProduct, setSelectedProduct] = useState('dataroom');
  const [templateContent, setTemplateContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [message, setMessage] = useState(null);

  // Product/module configuration
  const products = [
    { 
      id: 'dataroom', 
      name: 'Dataroom', 
      color: 'bg-blue-500',
      templateName: 'user_invite_dataroom',
      subject: 'You\'re Invited! - Access Dataroom on Kapitalized',
      description: 'Used when inviting users to dataroom projects'
    },
    { 
      id: 'capraise', 
      name: 'CapRaise', 
      color: 'bg-green-500',
      templateName: 'user_invite_capraise',
      subject: 'Investment Opportunity - You\'re Invited to Review on Kapitalized',
      description: 'Used when inviting users to capital raising rounds'
    },
    { 
      id: 'default', 
      name: 'Default', 
      color: 'bg-purple-500',
      templateName: 'user_invite_default',
      subject: 'You have been invited to Kapitalized',
      description: 'Used for general invitations and products without specific templates'
    }
  ];

  // Sample data for preview
  const previewData = {
    magic_link_url: 'https://kapitalized.com/accept-invite?token=sample123',
    current_year: new Date().getFullYear()
  };

  // Load template from API
  const loadTemplate = async (templateName) => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await fetch(`/api/email-templates?template=${templateName}`);
      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        setTemplateContent(data.content);
        setOriginalContent(data.content);
      } else {
        throw new Error(data.error || 'Failed to load template');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      setMessage({ type: 'error', text: `Failed to load template: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  // Save template to API
  const saveTemplate = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const selectedProductData = products.find(p => p.id === selectedProduct);
      const response = await fetch('/api/email-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_name: selectedProductData.templateName,
          content: templateContent
        })
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        setOriginalContent(templateContent);
        setEditing(false);
        setMessage({ type: 'success', text: 'Template saved successfully!' });
      } else {
        throw new Error(data.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: `Failed to save template: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Process template with variables for preview
  const processTemplate = (html, data = previewData) => {
    if (!html) return '';
    
    let processed = html;
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processed = processed.replace(regex, data[key] || '');
    });

    return processed;
  };

  // Handle product change
  const handleProductChange = (productId) => {
    if (editing && templateContent !== originalContent) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to switch products?')) {
        return;
      }
    }
    setSelectedProduct(productId);
    setEditing(false);
    setPreviewMode(false);
  };

  // Load template when product changes
  useEffect(() => {
    const selectedProductData = products.find(p => p.id === selectedProduct);
    if (selectedProductData) {
      loadTemplate(selectedProductData.templateName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const selectedProductData = products.find(p => p.id === selectedProduct);
  const processedHtml = processTemplate(templateContent);
  const hasChanges = templateContent !== originalContent;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Mail className="w-8 h-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Email Templates</h1>
        </div>
        <p className="text-gray-600">
          Edit and preview system email templates for user invitations
        </p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success'
            ? <CheckCircle className="h-5 w-5 mr-2" />
            : <AlertCircle className="h-5 w-5 mr-2" />
          }
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Product Selector */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Select Module</h2>
              <p className="text-sm text-gray-600 mt-1">Choose a module to edit its email template</p>
            </div>
            
            <div className="p-4">
              <div className="space-y-2">
                {products.map((product) => {
                  const isSelected = selectedProduct === product.id;
                  
                  return (
                    <button
                      key={product.id}
                      onClick={() => handleProductChange(product.id)}
                      className={`w-full p-3 rounded-lg text-left transition-all border ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${product.color}`}></div>
                        <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {product.name}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Variables Reference */}
          <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-3 flex items-center text-sm">
              <FileText className="w-4 h-4 mr-2" />
              Available Variables
            </h3>
            <div className="space-y-2 text-xs text-blue-800">
              <p><code className="bg-blue-100 px-1 rounded">{'{{magic_link_url}}'}</code> - Invitation acceptance link</p>
              <p><code className="bg-blue-100 px-1 rounded">{'{{current_year}}'}</code> - Current year</p>
            </div>
          </div>
        </div>

        {/* Template Editor/Preview */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedProductData?.name} Template
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedProductData?.description}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {!editing && !previewMode && (
                    <>
                      <button
                        onClick={() => setPreviewMode(true)}
                        className="flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </button>
                      <button
                        onClick={() => setEditing(true)}
                        className="flex items-center px-3 py-2 text-sm text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    </>
                  )}
                  {editing && (
                    <>
                      <button
                        onClick={() => {
                          setEditing(false);
                          setTemplateContent(originalContent);
                        }}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveTemplate}
                        disabled={saving || !hasChanges}
                        className={`flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                          saving || !hasChanges
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                  {previewMode && (
                    <button
                      onClick={() => setPreviewMode(false)}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <X className="h-4 w-4 mr-1 inline" />
                      Close Preview
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : previewMode ? (
                // Preview Mode
                <div>
                  {/* Email Subject */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-medium text-gray-700 mb-1">Email Subject:</div>
                    <div className="text-sm text-gray-900">{selectedProductData?.subject}</div>
                  </div>

                  {/* Email From */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-medium text-gray-700 mb-1">From:</div>
                    <div className="text-sm text-gray-900">
                      Kapitalized &lt;noreply@kapitalized.com&gt;
                    </div>
                    <p className="text-xs text-gray-500 mt-1">From name is configured in AdminHQ → User Invites</p>
                  </div>

                  {/* Email Body Preview */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-sm text-gray-600">Email Body Preview</span>
                      <span className="text-xs text-gray-500">Sample data used for preview</span>
                    </div>
                    <div 
                      className="max-h-[600px] overflow-y-auto bg-white"
                      dangerouslySetInnerHTML={{ __html: processedHtml }}
                    />
                  </div>
                </div>
              ) : editing ? (
                // Edit Mode
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template File: <code className="text-xs bg-gray-100 px-1 rounded">{selectedProductData?.templateName}.html</code>
                    </label>
                    <textarea
                      value={templateContent}
                      onChange={(e) => setTemplateContent(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      rows="25"
                      placeholder="Enter HTML template content..."
                    />
                    {hasChanges && (
                      <p className="mt-2 text-xs text-amber-600 flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        You have unsaved changes
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                // View Mode (read-only)
                <div>
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-medium text-gray-700 mb-1">Template File:</div>
                    <div className="text-sm text-gray-900 font-mono">
                      api/email_templates/{selectedProductData?.templateName}.html
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <span className="text-sm text-gray-600">Template Content (Read-only)</span>
                    </div>
                    <pre className="p-4 bg-white max-h-[600px] overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap">
                      {templateContent}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Template Management</p>
            <p>Templates are stored in the file system at <code className="bg-blue-100 px-1 rounded">api/email_templates/</code>. Changes are saved directly to these files and will be used immediately for all new invitations.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
